import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';

/**
 * Referenzen (ADR 0101): eigenständige Anlagen-Referenzen für den Vertrieb,
 * getrennt von `Project`. Ein Projekt kann per „In Referenz umwandeln"-Button
 * eine Referenz erzeugen (Snapshot, keine Live-Kopplung); Referenzen können
 * auch eigenständig angelegt werden (die meisten Altdaten-Referenzen haben
 * keinen Projekt-Bezug).
 */

export const referenceCategorySchema = z.enum(['KOMMUNAL', 'INDUSTRIE', 'KLAERANLAGE']);
export type ReferenceCategory = z.infer<typeof referenceCategorySchema>;

export const REFERENCE_CATEGORY_LABELS: Record<ReferenceCategory, string> = {
  KOMMUNAL: 'Kommunal',
  INDUSTRIE: 'Industrie',
  KLAERANLAGE: 'Kläranlage',
};
export const REFERENCE_CATEGORY_LABELS_EN: Record<ReferenceCategory, string> = {
  KOMMUNAL: 'Municipal',
  INDUSTRIE: 'Industrial',
  KLAERANLAGE: 'Wastewater treatment',
};

export function formatReferenceCategory(
  category: ReferenceCategory | null | undefined,
  lang: 'de' | 'en' = 'de',
): string {
  if (!category) return '';
  return (lang === 'en' ? REFERENCE_CATEGORY_LABELS_EN : REFERENCE_CATEGORY_LABELS)[category] ?? category;
}

const referenceFieldsSchema = {
  projectId: z.string().uuid().nullable().optional(),
  customerName: z.string().trim().max(200).optional(),
  contactPerson: z.string().trim().max(200).optional(),
  telephoneNo: z.string().trim().max(60).optional(),
  country: z.string().trim().max(120).optional(),
  location: z.string().trim().max(200).optional(),
  coordinates: z.string().trim().max(200).optional(),
  category: referenceCategorySchema.optional(),
  plantType: z.string().trim().max(120).optional(),
  product: z.string().trim().max(200).optional(),
  basinCount: z.number().int().min(0).max(1000).optional(),
  lengthM: z.number().min(0).max(100000).optional(),
  widthM: z.number().min(0).max(100000).optional(),
  heightM: z.number().min(0).max(100000).optional(),
  fromYear: z.number().int().min(1900).max(2100).optional(),
  toYear: z.number().int().min(1900).max(2100).optional(),
  notes: z.string().trim().max(2000).optional(),
};

export const createReferenceSchema = z.object(referenceFieldsSchema);
export type CreateReferenceInput = z.infer<typeof createReferenceSchema>;

export const updateReferenceSchema = z.object(referenceFieldsSchema).partial();
export type UpdateReferenceInput = z.infer<typeof updateReferenceSchema>;

export const referenceResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  contactPerson: z.string().nullable(),
  telephoneNo: z.string().nullable(),
  country: z.string().nullable(),
  location: z.string().nullable(),
  coordinates: z.string().nullable(),
  category: referenceCategorySchema.nullable(),
  plantType: z.string().nullable(),
  product: z.string().nullable(),
  basinCount: z.number().int().nullable(),
  lengthM: z.number().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  fromYear: z.number().int().nullable(),
  toYear: z.number().int().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ReferenceResponse = z.infer<typeof referenceResponseSchema>;

/** Filter/Query für die Referenzenliste — Muster `projectListQuerySchema`. */
export const referenceListQuerySchema = listQuerySchema.extend({
  countries: z.string().trim().max(2000).optional(), // kommagetrennt
  plantType: z.string().trim().max(120).optional(),
  product: z.string().trim().max(200).optional(),
  category: referenceCategorySchema.optional(),
  yearFrom: z.coerce.number().int().min(1900).max(2100).optional(),
  yearTo: z.coerce.number().int().min(1900).max(2100).optional(),
  lengthMin: z.coerce.number().min(0).optional(),
  lengthMax: z.coerce.number().min(0).optional(),
  widthMin: z.coerce.number().min(0).optional(),
  widthMax: z.coerce.number().min(0).optional(),
  heightMin: z.coerce.number().min(0).optional(),
  heightMax: z.coerce.number().min(0).optional(),
  /** Nur Referenzen dieses Projekts (Rückrichtung der Verlinkung, §4 ADR 0101). */
  projectId: z.string().uuid().optional(),
});
export type ReferenceListQuery = z.infer<typeof referenceListQuerySchema>;

export const referenceFacetSchema = z.object({ value: z.string(), count: z.number().int() });
export const referenceFacetsSchema = z.object({
  countries: z.array(referenceFacetSchema),
  plantTypes: z.array(referenceFacetSchema),
  products: z.array(referenceFacetSchema),
});
export type ReferenceFacets = z.infer<typeof referenceFacetsSchema>;
